import { VALID_IDENTIFIER_RE } from '@views/Logs/utils/normalize-facet';
import { entityProjectionNames } from './entityProjectionNames';

export type RelatedEntityQueryConfig = {
  targetTable: string;
  relationshipField: string;
  relatedProjName: string;
  keyPrefix?: string;
};

/**
 * Configuration for entity relationship queries.
 * Maps projection names to their related entity query configurations.
 * This is used when querying related entities (e.g., findings for an exception)
 * instead of the main entity table.
 */
export const entityRelationshipQueryConfig: Record<string, RelatedEntityQueryConfig> = {
  [entityProjectionNames.Exception]: {
    targetTable: 'uber_findings',
    relationshipField: 'finding_rule_based_exception',
    relatedProjName: 'uber_findings',
    keyPrefix: 'finding',
  },
};

/**
 * Validate that a field name is a safe SQL identifier.
 * Only allows alphanumeric characters and underscores to prevent SQL injection.
 * @param fieldName - The field name to validate
 * @returns true if the field name is safe, false otherwise
 */
const isValidSqlIdentifier = (fieldName: string): boolean => VALID_IDENTIFIER_RE.test(fieldName);

/**
 * Get the related entity query configuration for a given projection name.
 * @param projectionName - The projection name to get configuration for
 * @returns The related entity query configuration, or null if not found or invalid
 */
export const getRelatedEntityQueryConfig = (projectionName: string): RelatedEntityQueryConfig | null => {
  const config = entityRelationshipQueryConfig[projectionName];
  if (!config) {
    return null;
  }

  // Validate that relationshipField is a safe SQL identifier
  if (!isValidSqlIdentifier(config.relationshipField)) {
    console.error(`Invalid relationshipField in config: ${config.relationshipField}`);
    return null;
  }

  return config;
};
